import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_URL, absoluteUrl, structuredData } from '@/lib/seo';

export interface AdvancedSEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  keywords?: string[];
  noindex?: boolean;
  breadcrumbs?: Array<{ name: string; url: string }>;
  jsonLd?: any[];
}

/**
 * Advanced SEO Head Component - Unified SEO management
 * Handles canonical URLs, Open Graph, Twitter Cards, JSON-LD, and more
 */
export function AdvancedSEOHead({
  title,
  description,
  canonical,
  ogImage = '/og/default-1200x630.jpg',
  ogType = 'website',
  keywords = [],
  noindex = false,
  breadcrumbs = [],
  jsonLd = []
}: AdvancedSEOHeadProps) {
  const location = useLocation();
  
  // Detect production environment
  const isProduction = typeof window !== 'undefined' && (() => {
    const hostname = window.location.hostname;
    return (
      hostname === 'cosmos-wave.com' || 
      hostname === 'www.cosmos-wave.com' ||
      (!hostname.includes('lovable.app') && !hostname.includes('localhost'))
    );
  })();
  
  // Generate canonical URL - always use apex domain
  const canonicalUrl = canonical || absoluteUrl(location.pathname || '/');
  
  // Complete title with site name if not already included
  const fullTitle = title.includes('Cosmos Wave') ? title : `${title} | Cosmos Wave`;
  
  // Absolute OG image URL  
  const absoluteOgImage = ogImage.startsWith('http') ? ogImage : absoluteUrl(ogImage);
  
  // Robots meta - noindex on staging
  const robotsMeta = noindex || !isProduction ? 'noindex,nofollow' : 'index,follow';
  
  // Keywords as string
  const keywordsString = keywords.length > 0 ? keywords.join(', ') : undefined;

  // Generate BreadcrumbList schema
  const breadcrumbSchema = breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": absoluteUrl("/")
      },
      ...breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 2,
        "name": crumb.name,
        "item": absoluteUrl(crumb.url)
      }))
    ]
  } : null;

  // Base JSON-LD Graph with Organization and Website (only on homepage)
  const isHomepage = location.pathname === '/' || location.pathname === '';
  const baseJsonLd = isHomepage ? {
    "@context": "https://schema.org",
    "@graph": [
      structuredData.organization,
      structuredData.website,
      {
        "@type": "WebPage",
        "url": canonicalUrl,
        "name": fullTitle,
        "description": description,
        "isPartOf": {
          "@type": "WebSite",
          "url": SITE_URL
        }
      }
    ]
  } : {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "url": canonicalUrl,
    "name": fullTitle,
    "description": description,
    "isPartOf": {
      "@type": "WebSite",
      "url": SITE_URL
    }
  };

  return (
    <Helmet>
      {/* HTML attributes */}
      <html lang="fr" />
      
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      <meta name="robots" content={robotsMeta} />
      
      {/* Keywords (optional) */}
      {keywordsString && <meta name="keywords" content={keywordsString} />}
      
      {/* Security & Performance Headers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      
      {/* Preconnect for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Cosmos Wave" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - Cosmos Wave`} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />
      <meta name="twitter:url" content={canonicalUrl} />
      
      {/* Base JSON-LD (Organization + Website + WebPage) */}
      <script type="application/ld+json">
        {JSON.stringify(baseJsonLd)}
      </script>
      
      {/* Breadcrumbs Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {/* Additional JSON-LD schemas */}
      {jsonLd.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default AdvancedSEOHead;